import React from 'react';

import MultiGrid, { MultiGridCellRendererProps } from '../MultiGrid/MultiGrid';
import SkeletonCard from '../Skeleton/SkeletonCard';

import { action, computed, makeObservable, observable } from 'mobx';
import DataGridBodyCell from './DataGrid.Cells/DataGrid.BodyCell';
import DataGridHeaderCell from './DataGrid.Cells/DataGrid.HeaderCell';
import injectStores from '../RootStore/injectStores';

import { DataGridProps } from 'types/DataGridTypes';
import RootStore from 'stores/RootStore';

class DataGrid extends React.PureComponent<DataGridProps> {
  namespaceId = '';
  constructor(props: DataGridProps) {
    super(props);
    makeObservable(this, {
      namespaceId: observable,
      setNamespaceId: action,
    });

    this.setupDataGrid();
  }

  setNamespaceId = (newNamespaceId: string) => {
    if (this.props.debugMode)
      console.log('Setting namespaceId', newNamespaceId);
    this.namespaceId = newNamespaceId;
  };

  setupDataGrid = () => {
    const {
      dataGridStore,
      storeName,
      uniqueKey,
      columnHeaders,
      defaultFilter,
      defaultSort,
      customEntity,
      namespace,
    } = this.props;
    if (!dataGridStore) {
      throw new Error('DataGridStore is required');
    }

    const namespaceId = dataGridStore.setupNamespace(namespace);
    this.setNamespaceId(namespaceId);
    const currentDataGridStore = dataGridStore.namespaces[namespaceId];

    if (storeName) {
      currentDataGridStore.setCurrentStoreName(storeName);
    }
    if (uniqueKey) {
      currentDataGridStore.setUniqueKey(uniqueKey);
    }
    if (columnHeaders) {
      currentDataGridStore.setColumnHeaders(columnHeaders);
    }
    if (defaultFilter) {
      currentDataGridStore.setDefaultFilter(
        defaultFilter as Record<string, string>
      );
    }
    if (defaultSort) {
      currentDataGridStore.setDefaultSort({
        sortBy: [...defaultSort.sortBy],
        sortDirection: { ...defaultSort.sortDirection },
      });
    }
    if (customEntity) {
      currentDataGridStore.setEntityValue(customEntity);
    }
  };

  componentDidMount() {
    addEventListener('keydown', this.keyPressHandler, false);
  }

  componentWillUnmount() {
    removeEventListener('keydown', this.keyPressHandler, false);
    const { autoReset = false } = this.props;
    if (autoReset) {
      if (this.namespaceId) {
        const { dataGridStore } = this.props;
        if (!dataGridStore) {
          throw new Error('DataGridStore is required');
        }
        if (this.props.debugMode)
          console.log('Clearing namespaceId', this.namespaceId);
        dataGridStore.clearNamespace(this.namespaceId);
      }
    }
  }

  keyPressHandler = (event: KeyboardEvent) => {
    const { onSubmitEdits, onRemove, dataGridStore, rootStore } = this.props;
    if (!dataGridStore) {
      throw new Error('DataGridStore is required');
    }
    const currentStore = dataGridStore.namespaces[this.namespaceId];
    if (event.key === 'Escape') {
      event.preventDefault();
      event.stopPropagation();

      currentStore?.resetEditData();

      if (currentStore?.removeColumnEnabled) {
        currentStore?.toggleRemoveColumn();
      }
    }
    if (event.key === 'Enter') {
      event.preventDefault();
      event.stopPropagation();
      Object.entries(currentStore?.editData).map(([key, value]) => {
        Object.entries(
          value as { [key: string]: boolean | string | number }
        ).map(([changedKey, changedValue]) => {
          if (changedValue === 'True') {
            value[changedKey] = true;
          }
          if (changedValue === 'False') {
            value[changedKey] = false;
          }
        });

        if (onSubmitEdits) {
          onSubmitEdits(key, value, rootStore as RootStore);
        }
        currentStore?.removeEditData(key);
      });
    }
    if (onRemove && event.ctrlKey && event.key === 'Delete') {
      currentStore?.toggleRemoveColumn();
    }
  };

  getColumnWidth = ({ index }: { index: number }) => {
    const { columnWidth = 150, dataGridStore } = this.props;
    if (!dataGridStore) {
      throw new Error('DataGridStore is required');
    }
    const currentStore = dataGridStore.namespaces[this.namespaceId];
    if (
      currentStore?.currentColumnHeaders[index] &&
      currentStore?.currentColumnHeaders[index] &&
      'width' in currentStore.currentColumnHeaders[index]
    ) {
      return currentStore?.currentColumnHeaders[index].width as number;
    }
    return columnWidth;
  };

  // get currentStore() {
  //   console.log('this.namespaceId', this.namespaceId);
  //   console.log('this.props', this.props);
  //   // const { dataGridStore } = this.props;
  //   const dataGridStore = this.props.dataGridStore;
  //   const { namespaces } = dataGridStore;
  //   return namespaces[this.namespaceId];
  // }

  get saveLocation() {
    if (!this.props.enableSaveLocation) {
      return undefined;
    }
    const routerStore = this.props.rootStore?.routerStore;
    return `${this.namespaceId}_${routerStore?.currentPath || 'default'}_saveLocation`;
  }

  render() {
    const {
      height,
      width,
      loading = false,
      rowHeight = 50,
      fixedColumnCount = 0,
      fixedRowCount = 2,
      onRowClick,
      enableHover = true,
      enableInlineEdit = false,
      enableFullGridEdit,
      onSubmitEdits,
      dataGridStore,
      onRemove,
      rootStore,
    } = this.props;

    if (!dataGridStore) {
      throw new Error('DataGridStore is required');
    }

    // console.log('loading', loading);
    // console.log('dataGridStore', dataGridStore);
    // console.log('this.namespaceId', this.namespaceId);
    const currentStore = dataGridStore.namespaces[this.namespaceId];

    // console.log('currentStore', currentStore);

    if (loading || !this.namespaceId || !currentStore) {
      // console.log('returning skeleton');
      return <SkeletonCard height={height} width={width} />;
    }

    const currentHeight = computed(() => {
      if (height) {
        return height;
      }
      return rootStore?.viewStore?.cardBodySize?.height;
    }).get();

    const currentWidth = computed(() => {
      if (width) {
        return width;
      }
      return rootStore?.viewStore?.cardBodySize?.width || 500;
    }).get();

    const currentRows = computed(
      () =>
        currentStore?.currentRows.map((i: object) => {
          return { ...i };
        }) || []
    ).get();

    return (
      <div
        style={{
          height: currentHeight,
          width: currentWidth,
        }}
        onMouseLeave={() => {
          currentStore?.resetCellHover();
        }}
      >
        <MultiGrid
          height={currentHeight}
          width={currentWidth}
          rowHeight={rowHeight}
          columnWidth={this.getColumnWidth}
          enableFixedRowScroll={true}
          enableFixedColumnScroll={true}
          data={currentRows}
          columnKeys={currentStore?.currentColumnKeys || []}
          fixedColumnCount={fixedColumnCount}
          fixedRowCount={fixedRowCount}
          setRef={(ref: unknown) => ref && typeof ref === 'object' && ref !== null && 'recomputeGridSize' in ref && currentStore?.setDataGridRef(ref)}
          saveLocation={this.saveLocation}
          leftHeaderCellRenderer={(props: MultiGridCellRendererProps) => {
            const { key, ...rest } = props;
            // console.log('leftHeaderCellRenderer', props);
            return (
              <DataGridHeaderCell
                key={key}
                {...rest}
                namespaceId={this.namespaceId}
                style={props.style}
              />
            );
          }}
          rightHeaderCellRenderer={(props: MultiGridCellRendererProps) => {
            const { key, ...rest } = props;
            // console.log('rightHeaderCellRenderer', props);
            return (
              <DataGridHeaderCell
                key={key}
                {...rest}
                namespaceId={this.namespaceId}
                style={props.style}
              />
            );
          }}
          leftBodyCellRenderer={(props: MultiGridCellRendererProps) => {
            const { key, ...rest } = props;
            // console.log('leftBodyCellRenderer', props);

            return (
              <DataGridBodyCell
                key={key}
                {...rest}
                namespaceId={this.namespaceId}
                onRowClick={onRowClick}
                enableHover={enableHover}
                enableInlineEdit={enableInlineEdit}
                enableFullGridEdit={enableFullGridEdit}
                onSubmitEdits={onSubmitEdits}
                onRemove={onRemove}
                style={props.style}
              />
            );
          }}
          rightBodyCellRenderer={(props: MultiGridCellRendererProps) => {
            const { key, ...rest } = props;
            // console.log('rightBodyCellRenderer', props);
            return (
              <DataGridBodyCell
                key={key}
                {...rest}
                namespaceId={this.namespaceId}
                onRowClick={onRowClick}
                enableHover={enableHover}
                enableInlineEdit={enableInlineEdit}
                enableFullGridEdit={enableFullGridEdit}
                onSubmitEdits={onSubmitEdits}
                onRemove={onRemove}
                style={props.style}
              />
            );
          }}
        />
      </div>
    );
  }
}

const DataGridWithStores = injectStores(DataGrid);
export default DataGridWithStores;
